import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import FormSubmissionButtons from '../../FormSubmissionButtons';
import { ROUTES } from '@/constants/routes';
import { useForm } from 'react-hook-form';
import { SVButton, SVForm } from '../../core';
import { AddWireRecipientForm } from '@/types/wireRecipients';
import WireRecipientsBankInfo from './WireRecipientFormBankInfo';
import WireRecipientFormRecipientInfo from './WireRecipientFormRecipientInfo';
import { useAddWireRecipient } from '@/hooks/requests/useWireRecipients';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

type WireRecipientAddFormProps = {
  customerId: string;
  onLeave: () => void;
  isModal?: boolean;
};

export default function WireRecipientAddForm({
  customerId,
  onLeave,
  isModal,
}: WireRecipientAddFormProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const formMethods = useForm<AddWireRecipientForm>({
    defaultValues: {
      customerId,
      recipientInfo: {
        nickName: '',
        name: '',
        internationalAddress: false,
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: '',
        zip: '',
      },
      bankInfo: {
        bankName: '',
        accountType: '',
        accountNumber: '',
        confirmAccountNumber: '',
        routingNumber: '',
        confirmRoutingNumber: '',
        internationalBank: false,
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: '',
        zip: '',
      },
    },
    mode: 'onBlur',
  });

  const { addWireRecipient, isAddWireRecipientPending } = useAddWireRecipient();

  async function onSubmit() {
    const data = formMethods.getValues();
    try {
      await addWireRecipient(data);
      showToast({
        type: 'success',
        data: {
          content:
            'The wire recipient request has been sent to the customer for approval.',
        },
      });
      if (isModal) {
        onLeave();
      } else {
        navigate(`${ROUTES.CUSTOMERS}/${customerId}${ROUTES.WIRE_RECIPIENTS}`);
      }
    } catch (error: any) {
      formMethods.setError('recipientInfo.nickName', {
        type: 'custom',
        message: MESSAGES.duplicateNickname,
      });
      showToast({
        type: 'error',
        data: {
          content:
            error.response?.status === 422
              ? MESSAGES.duplicateNickname
              : MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <SVForm<AddWireRecipientForm>
      methods={formMethods}
      onSubmit={() => setOpen(true)}>
      <Stack spacing={2}>
        <WireRecipientFormRecipientInfo />
        <WireRecipientsBankInfo />
        <FormSubmissionButtons
          isLoading={isAddWireRecipientPending}
          onLeave={onLeave}
        />
      </Stack>
      <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
        <DialogContent>
          <Box>
            <Typography variant="h6" component="h2">
              Add Wire Recipient Confirmation
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Are you sure you would like to send the wire recipient request to the
              customer?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <SVButton onClick={() => setOpen(false)} variant="tertiary">
            Cancel
          </SVButton>
          <SVButton onClick={onSubmit}>Yes, Send Wire Recipient</SVButton>
        </DialogActions>
      </Dialog>
    </SVForm>
  );
}
